/**
 * OAuth 2.0 client with PKCE for "Login with BalesOtomatis.id".
 *
 * Browser-side responsibilities:
 *   1. Generate code_verifier + code_challenge (PKCE S256)
 *   2. Persist verifier + state in sessionStorage (survives redirect)
 *   3. Redirect user to auth.balesotomatis.id/oauth/authorize
 *   4. Receive code at /auth/callback?code=...&state=...
 *   5. Hand code + verifier to backend /api/playground/auth/exchange
 *      which then talks to /oauth/token and sets HttpOnly cookie.
 *
 * Refresh tokens NEVER touch JS — the backend bridge swaps cookies on
 * behalf of the SPA. That's the whole point of HttpOnly + bridge.
 */

const AUTH_BASE = (import.meta.env.VITE_AUTH_URL as string | undefined) ||
  'https://auth.balesotomatis.co.id';
const PLAYGROUND_BASE = (import.meta.env.VITE_PLAYGROUND_URL as string | undefined) ||
  window.location.origin;
const CLIENT_ID = (import.meta.env.VITE_OAUTH_CLIENT_ID as string | undefined) ||
  'playground-balesotomatis-id';

const STORAGE_PREFIX = 'bo_oauth_';
const VERIFIER_KEY = STORAGE_PREFIX + 'verifier';
const STATE_KEY = STORAGE_PREFIX + 'state';
const RETURN_TO_KEY = STORAGE_PREFIX + 'return_to';

const DEFAULT_SCOPES = [
  'openid',
  'profile',
  'email',
  'offline_access',
  'read:chats',
  'read:messages',
  'read:contacts',
  'read:campaigns',
  'read:flows',
];

// ── PKCE helpers ────────────────────────────────────────────────────────

function base64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  // 32 random bytes → 43 char base64url, well within PKCE spec range (43..128)
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes.buffer);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64url(digest);
}

// ── Public API ──────────────────────────────────────────────────────────

export interface LoginOptions {
  /** Path to come back to after login completes. Default: current page. */
  returnTo?: string;
  /** Override the default scope set. */
  scope?: string[];
  /** Force re-authentication even if user has a session. */
  prompt?: 'login' | 'consent' | 'none';
}

/**
 * Kick off the OAuth flow: build PKCE pair, store state, redirect.
 * The page is replaced — no callback to wait for here.
 */
export async function startLogin(options: LoginOptions = {}): Promise<void> {
  const verifier = randomVerifier();
  const challenge = await sha256Challenge(verifier);
  const state = randomVerifier();

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_TO_KEY, options.returnTo || window.location.pathname + window.location.search);

  const redirectUri = `${PLAYGROUND_BASE}/auth/callback`;
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: redirectUri,
    scope: (options.scope || DEFAULT_SCOPES).join(' '),
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  if (options.prompt) params.set('prompt', options.prompt);

  window.location.assign(`${AUTH_BASE}/oauth/authorize?${params.toString()}`);
}

export interface CallbackResult {
  ok: boolean;
  error?: string;
  errorDescription?: string;
  returnTo: string;
}

/**
 * Handle the /auth/callback redirect. Parses code+state, verifies state
 * matches the one we stored, then asks the playground backend to perform
 * the code-for-token exchange (so refresh token stays HttpOnly).
 */
export async function handleCallback(searchParams: URLSearchParams): Promise<CallbackResult> {
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/';
  sessionStorage.removeItem(RETURN_TO_KEY);

  // Server-reported error
  const err = searchParams.get('error');
  if (err) {
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    return {
      ok: false,
      error: err,
      errorDescription: searchParams.get('error_description') || undefined,
      returnTo,
    };
  }

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);

  // Clean up immediately so we never re-use these
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  if (!code || !state || !expectedState || !verifier) {
    return { ok: false, error: 'invalid_state', errorDescription: 'Missing OAuth state.', returnTo };
  }
  if (state !== expectedState) {
    return { ok: false, error: 'state_mismatch', errorDescription: 'CSRF state mismatch.', returnTo };
  }

  // Hand off to backend bridge to keep refresh token in HttpOnly cookie.
  const exchangeUrl = '/api/playground/auth/exchange';
  let res: Response;
  try {
    res = await fetch(exchangeUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        code_verifier: verifier,
        redirect_uri: `${PLAYGROUND_BASE}/auth/callback`,
      }),
    });
  } catch (e) {
    return { ok: false, error: 'network_error', errorDescription: (e as Error).message, returnTo };
  }

  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error_description || '';
    } catch {
      /* ignore */
    }
    return { ok: false, error: 'exchange_failed', errorDescription: detail || `HTTP ${res.status}`, returnTo };
  }

  return { ok: true, returnTo };
}

/**
 * Logout: hit backend to clear cookie + revoke refresh token, then redirect home.
 */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
  } catch {
    /* ignore */
  }
  window.location.assign('/');
}
